/**
 * Argus Admin Tab — AI-assisted hotel verification review
 * Shows Hermes Agent findings, Argus recommendations, one-click approve/reject
 */
import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import {
  Eye, Shield, CheckCircle, XCircle, AlertTriangle, Loader2,
  Hotel, MapPin, Globe, BarChart3, RefreshCw, ChevronDown,
  ChevronUp, Coins, Clock, FileSearch,
} from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL, API } from '@/lib/backendUrl';


const SCORE_COLORS = {
  high: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20' },
  mid: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20' },
  low: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/20' },
};

const getScoreStyle = (score) => score >= 85 ? SCORE_COLORS.high : score >= 50 ? SCORE_COLORS.mid : SCORE_COLORS.low;

function ScoreDimension({ label, score, maxScore, notes }) {
  const pct = (score / maxScore) * 100;
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-[10px] text-gray-400 uppercase tracking-wider">{label}</span>
        <span className={`text-xs font-semibold tabular-nums ${pct >= 70 ? 'text-emerald-400' : pct >= 40 ? 'text-amber-400' : 'text-red-400'}`}>
          {score}/{maxScore}
        </span>
      </div>
      <Progress value={pct} className="h-1" />
      {notes && <p className="text-[10px] text-gray-500 leading-relaxed">{notes}</p>}
    </div>
  );
}

function HotelReviewCard({ review, onApprove, onReject, approving }) {
  const [expanded, setExpanded] = useState(false);
  const hotel = review.hotel || {};
  const ai = review.ai_analysis || {};
  const analysis = ai.analysis || {};
  const score = ai.confidence_score || 0;
  const style = getScoreStyle(score);

  const riskFlags = analysis.risk_flags || [];

  // Argus recommendation
  let argusRec = 'Approve with monitoring';
  let argusColor = 'text-amber-400';
  if (score >= 75) { argusRec = 'Recommend approval'; argusColor = 'text-emerald-400'; }
  else if (score < 50) { argusRec = 'Recommend rejection'; argusColor = 'text-red-400'; }

  return (
    <Card className="bg-white/[0.02] border-white/5 hover:border-white/10 transition-colors" data-testid={`review-card-${hotel.id}`}>
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`w-10 h-10 rounded-xl ${style.bg} flex items-center justify-center shrink-0`}>
              <span className={`text-lg font-bold heading-font tabular-nums ${style.text}`}>{score}</span>
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-white heading-font truncate">{hotel.name || 'Unknown Hotel'}</h3>
              <div className="flex items-center gap-2 text-[10px] text-gray-500">
                <MapPin className="w-3 h-3" />
                <span>{hotel.city || '?'}, {hotel.country || '?'}</span>
                <span className="text-gray-700">|</span>
                <Clock className="w-3 h-3" />
                <span>{hotel.created_at ? new Date(hotel.created_at).toLocaleDateString() : 'N/A'}</span>
              </div>
            </div>
          </div>
          <Badge className={`${style.bg} ${style.text} ${style.border} text-[10px] shrink-0`}>
            {ai.decision === 'needs_review' ? 'Needs Review' : ai.decision === 'auto_reject' ? 'Rejected' : ai.decision}
          </Badge>
        </div>

        {/* Argus Recommendation */}
        <div className="mt-3 flex items-center gap-2 bg-white/[0.02] border border-white/5 rounded-lg px-3 py-2" data-testid="argus-recommendation">
          <Eye className="w-4 h-4 text-teal-400 shrink-0" />
          <div className="flex-1">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider">Argus Recommends</p>
            <p className={`text-xs font-medium ${argusColor}`}>{argusRec}</p>
          </div>
        </div>

        {/* Risk flags */}
        {riskFlags.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {riskFlags.map((flag, i) => (
              <Badge key={i} variant="outline" className="text-[9px] border-red-500/20 text-red-400 bg-red-500/5">
                <AlertTriangle className="w-2.5 h-2.5 mr-1" />
                {flag}
              </Badge>
            ))}
          </div>
        )}

        {/* Summary */}
        <p className="mt-2 text-xs text-gray-400 leading-relaxed line-clamp-2">
          {analysis.summary || 'No summary available'}
        </p>

        {/* Expand / Collapse */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-2 flex items-center gap-1 text-[10px] text-gray-500 hover:text-gray-300 transition-colors"
          data-testid="expand-details"
        >
          <FileSearch className="w-3 h-3" />
          {expanded ? 'Hide' : 'View'} Hermes Agent Analysis
          {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>

        {expanded && (
          <div className="mt-3 space-y-3 pt-3 border-t border-white/5" data-testid="expanded-analysis">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="w-3.5 h-3.5 text-[#F7931A]" />
              <span className="text-[10px] text-gray-500 uppercase tracking-wider">Hermes Agent Scoring</span>
            </div>
            <ScoreDimension label="Business Legitimacy" score={analysis.business_legitimacy?.score || 0} maxScore={25} notes={analysis.business_legitimacy?.notes} />
            <ScoreDimension label="Location Consistency" score={analysis.location_consistency?.score || 0} maxScore={25} notes={analysis.location_consistency?.notes} />
            <ScoreDimension label="Online Presence" score={analysis.online_presence?.score || 0} maxScore={25} notes={analysis.online_presence?.notes} />
            <ScoreDimension label="Photo Legitimacy" score={analysis.photo_legitimacy?.score || 0} maxScore={25} notes={analysis.photo_legitimacy?.notes} />
          </div>
        )}

        {/* Actions */}
        <div className="mt-3 pt-3 border-t border-white/5 flex gap-2">
          <Button
            size="sm"
            className="flex-1 bg-emerald-600 hover:bg-emerald-500"
            onClick={() => onApprove(hotel.id)}
            disabled={approving}
            data-testid={`approve-${hotel.id}`}
          >
            {approving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle className="w-3 h-3 mr-1" />}
            Approve + Mint SBT
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="flex-1 border-red-500/30 text-red-400 hover:bg-red-500/10"
            onClick={() => onReject(hotel.id)}
            disabled={approving}
            data-testid={`reject-${hotel.id}`}
          >
            <XCircle className="w-3 h-3 mr-1" /> Reject
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function ArgusAdminTab() {
  const [stats, setStats] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, reviewsRes] = await Promise.all([
        fetch(`${API}/api/hermes/stats`).then(r => r.json()),
        fetch(`${API}/api/hermes/pending-reviews`).then(r => r.json()),
      ]);
      setStats(statsRes);
      setReviews(reviewsRes.reviews || []);
    } catch (err) {
      toast.error('Failed to load Argus data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleApprove = async (hotelId) => {
    setApproving(true);
    try {
      const res = await fetch(`${API}/api/hermes/admin-approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hotel_id: hotelId, admin_email: 'admin@hermes-protocol.com' }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Hotel approved! SBT minted: ${data.sbt_minted}, Tokenomics: ${data.tokenomics_triggered}`);
        fetchData();
      } else {
        toast.error(data.detail || 'Approval failed');
      }
    } catch (err) {
      toast.error('Failed to approve hotel');
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async (hotelId) => {
    try {
      await fetch(`${API}/api/hotels/${hotelId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ verification_status: 'rejected' }),
      });
      toast.success('Hotel rejected');
      fetchData();
    } catch (err) {
      toast.error('Failed to reject hotel');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-teal-400" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 lg:px-6 py-6 space-y-6" data-testid="argus-admin-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-teal-500/10 border border-teal-500/20 flex items-center justify-center">
            <Eye className="w-5 h-5 text-teal-400" />
          </div>
          <div>
            <h2 className="text-sm font-semibold heading-font text-white">Argus Admin Console</h2>
            <p className="text-[10px] text-gray-500">AI-assisted hotel verification + Hermes Agent findings</p>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={fetchData} className="border-white/10 text-gray-400" data-testid="refresh-argus">
          <RefreshCw className="w-3 h-3 mr-1" /> Refresh
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="argus-stats">
          {[
            { label: 'Total', value: stats.total_verifications, color: 'text-gray-300' },
            { label: 'Auto-Approved', value: stats.auto_approved, color: 'text-emerald-400' },
            { label: 'Needs Review', value: stats.needs_review, color: 'text-amber-400' },
            { label: 'Auto-Rejected', value: stats.auto_rejected, color: 'text-red-400' },
            { label: 'Admin Approved', value: stats.admin_approved, color: 'text-blue-400' },
            { label: 'Errors', value: stats.errors, color: 'text-gray-500' },
            { label: 'Avg Score', value: stats.avg_confidence_score, color: 'text-[#F7931A]' },
          ].map(s => (
            <div key={s.label} className="bg-white/[0.02] border border-white/5 rounded-lg p-3 text-center">
              <p className={`text-lg font-bold heading-font tabular-nums ${s.color}`}>{s.value}</p>
              <p className="text-[9px] text-gray-600 uppercase tracking-wider">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      <Separator className="bg-white/5" />

      {/* Pending Reviews */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <AlertTriangle className="w-4 h-4 text-amber-400" />
          <h3 className="text-xs font-semibold text-gray-300 uppercase tracking-wider">
            Hotels Awaiting Argus Review ({reviews.length})
          </h3>
        </div>

        {reviews.length === 0 ? (
          <Card className="bg-white/[0.02] border-white/5">
            <CardContent className="p-8 text-center">
              <CheckCircle className="w-8 h-8 text-emerald-500/50 mx-auto mb-3" />
              <p className="text-sm text-gray-400">All clear! No hotels awaiting review.</p>
              <p className="text-[10px] text-gray-600 mt-1">Hermes Agent is handling verifications autonomously.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 lg:grid-cols-2">
            {reviews.map((review, i) => (
              <HotelReviewCard
                key={review.hotel?.id || i}
                review={review}
                onApprove={handleApprove}
                onReject={handleReject}
                approving={approving}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
